import { useState } from "react";
import CloudProviderCard from "../cloud/CloudProviderCard";
import CloudFileBrowser from "../cloud/CloudFileBrowser";
import AgentSelection from "../AgentSelection";
import { useCloudImportMutation } from "@/hooks/uploads/useCloudImportMutation";
import { CloudFile, CloudProvider } from "@/types/cloud";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Loader2, X } from "lucide-react";
import { UploadComponentProps } from "./types";
import { useGoogleDrivePicker } from "@/hooks/cloud/useGoogleDrivePicker";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

type UploadCloudStorageProps = UploadComponentProps;

const UploadCloudStorage = ({
  availableAgents = [],
  selectedAgentId = "none",
  onAgentSelect = () => {},
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadCloudStorageProps) => {
  const [browserOpen, setBrowserOpen] = useState(false);
  const [activeProvider, setActiveProvider] = useState<CloudProvider>("google_drive");
  const [selectedFiles, setSelectedFiles] = useState<CloudFile[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const importMutation = useCloudImportMutation(onUploadSuccess);
  const googleDrivePicker = useGoogleDrivePicker();

  const handleFilesSelected = (files: CloudFile[]) => {
    setSelectedFiles(files);
    setBrowserOpen(false);
    setDrawerOpen(true);
  };

  const handleImport = async () => {
    if (selectedFiles.length === 0) return;

    // If using deferred flow (agent not created yet), queue the action
    if (onDeferredActionAdd && selectedAgentId === "none") {
      onDeferredActionAdd({
        type: "cloud-storage",
        data: {
          provider: activeProvider,
          files: selectedFiles,
        },
      });
      setSelectedFiles([]);
      setDrawerOpen(false);
      return;
    }

    // Otherwise, import directly with agent attachment
    importMutation.mutate(
      {
        provider: activeProvider,
        files: selectedFiles,
        agentId: selectedAgentId,
      },
      {
        onSuccess: () => {
          setSelectedFiles([]);
          setDrawerOpen(false);
        },
      }
    );
  };

  const handleGoogleDriveBrowse = async () => {
    setActiveProvider("google_drive");
    await googleDrivePicker.openPicker({
      fileIds: activeProvider === "google_drive" ? selectedFiles.map((file) => file.id) : [],
      onFilesPicked: (files) => {
        setSelectedFiles(files);
        setDrawerOpen(true);
      },
    });
  };

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="dashboard" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Cloud Storage</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Connect to Google Drive or OneDrive to import your documents
        </p>

        {/* Provider Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          <CloudProviderCard
            provider="google_drive"
            onBrowseClick={handleGoogleDriveBrowse}
            browseLabel={googleDrivePicker.isLoading ? "Opening Picker..." : "Pick Files"}
          />
          <CloudProviderCard
            provider="onedrive"
            onBrowseClick={() => {
              setActiveProvider("onedrive");
              setBrowserOpen(true);
            }}
          />
        </div>
      </div>

      {/* File Browser */}
      <CloudFileBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onFilesSelected={handleFilesSelected}
        provider={activeProvider}
      />

      {/* Side Drawer for Adding to KB */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>
              Add Files to Knowledge Base
              {selectedAgentId !== "none" && (
                <span className="block text-sm font-normal text-gray-600 mt-1">
                  for {availableAgents.find((a) => a.id === selectedAgentId)?.name || "Unknown Agent"}
                </span>
              )}
            </SheetTitle>
            <SheetDescription>
              Configure and add {selectedFiles.length} file{selectedFiles.length !== 1 ? "s" : ""} to your knowledge base
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto py-6">
            {/* Selected Files List */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto bg-gray-50 rounded-lg p-4">
                {selectedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      {file.size && (
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedFiles(selectedFiles.filter((f) => f.id !== file.id))}
                      className="ml-2 text-gray-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="border-t border-gray-200 pt-6 space-y-3">
            <Button
              variant="outline"
              onClick={() => setSelectedFiles([])}
              disabled={importMutation.isPending}
              className="w-full"
            >
              Clear Selection
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || selectedFiles.length === 0}
              className="w-full bg-gradient-to-r from-genie-primary to-genie-secondary text-white"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Adding to Knowledge Base...
                </>
              ) : (
                `Add ${selectedFiles.length} File${selectedFiles.length !== 1 ? "s" : ""}`
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default UploadCloudStorage;
